<template>
  <ERow>
    <ECol cols="12" class="py-0">
      <ReportTable
        name="projects"
        item-key="project.exid"
        :headers="projectHeaders"
        :provider="getGateReportProjectsInfos"
        :copy-to-clipboard="false"
        :hide-default-footer="true"
        show-expand
        :show-bookmarks="false"
        :hide-show="false"
        actions-portal-target="breadcrumbs-right"
        :single-expand="true"
      >
        <template #actions-right>
          <v-btn color="primary" small class="mr-1" @click="showDialog = true">
            <v-icon class="mr-2" small>fa-plus</v-icon> Create Application
          </v-btn>
          <AnprGateReportCreateUpdateAppDialog
            :value="showDialog"
            :edit-mode="isEditMode"
            :app-data="appData"
            @input="onCloseDialog"
          />
        </template>
        <template #item.name="{ item }">
          <div class="project-item">
            <div class="d-inline">
              <NdaIcon v-if="isNDAProject(item)" />
              <nuxt-link :to="getProjectLink(item)">
                <div class="d-inline-flex">
                  <ETruncatedDiv
                    class="cursor-pointer primary--text"
                    :width="isNDAProject(item.project) ? '8rem' : '9rem'"
                    fixed-width
                    >{{ item.project.name }}
                  </ETruncatedDiv>
                </div>
              </nuxt-link>
            </div>
            <v-btn
              icon
              color="primary"
              x-small
              @click="projectStore.openDialog(item.project.exid)"
            >
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon color="primary" v-bind="attrs" v-on="on">
                    fa-info-circle
                  </v-icon>
                </template>
                <span>View project details</span>
              </v-tooltip>
            </v-btn>
          </div>
        </template>
        <template #item.exid="{ item }">
          <span
            class="cursor-pointer primary--text"
            @click="projectStore.openDialog(item.project.exid)"
            >{{ item.project.exid }}
          </span>
        </template>
        <template #item.cameras="{ item }">
          <CamerasColumn
            :item="item"
            :items-per-line="1"
            @open-camera-dialog="onCameraDialogOpen"
          />
        </template>
        <template #item.contextCameras="{ item }">
          <CamerasColumn
            :item="{
              cameras: item.cameras.map((camera) => ({
                exid: camera.contextCameraExid,
              })),
            }"
            :items-per-line="1"
            @open-camera-dialog="onCameraDialogOpen"
          />
        </template>
        <template #item.cameraStatuses="{ item }">
          <span v-for="(camera, index) in item.cameras" :key="index">
            <span :class="getStatusColor(camera.status)">
              {{ getStatusText(camera.status) }}
            </span>
            <br />
          </span>
        </template>
        <template #item.timezone="{ item }">
          <span>
            {{ item.project.timezone }}
          </span>
        </template>
        <template #item.appsStatus="{ item }">
          <span v-for="(camera, index) in item.cameras" :key="index">
            <span :class="appsStatusColorText(camera.coolifyServiceStatus)">
              {{ camera.coolifyServiceStatus }}
            </span>
            <br />
          </span>
        </template>
        <template #item.postProcessingServers="{ item }">
          <span v-for="(camera, index) in item.cameras" :key="index">
            <span>
              {{ camera.coolifyServerName }}
            </span>
            <br />
          </span>
        </template>
        <template #item.appsVersion="{ item }">
          <span v-for="(camera, index) in item.cameras" :key="index">
            <span>
              {{ camera.branchName }}
            </span>
            <br />
          </span>
        </template>
        <template #item.featureFlags="{ item }">
          <EReadMore :content="getFeatureFlags(item.project)" :max-length="2" />
        </template>
        <template #expanded-item="{ headers, item }">
          <td :colspan="headers.length" style="box-shadow: none !important">
            <AnprGateReportCamerasTable
              :cameras="item.cameras"
              :project="item.project"
              :loading="false"
              @open-camera-dialog="onCameraDialogOpen"
              @edit-app-dialog="(camera) => onEditApp(item, camera)"
            />
          </td>
        </template>
      </ReportTable>
    </ECol>
  </ERow>
</template>

<script lang="ts">
import ReportTable from "@/components/ReportTable"
import { projectHeaders } from "@/components/gateReport/anpr/anprGateReportHeaders"
import AnprGateReportCamerasTable from "@/components/gateReport/anpr/AnprGateReportCamerasTable"
import CamerasColumn from "@/components/CamerasColumn"
import NdaIcon from "@/components/NdaIcon"
import { ProjectFeatureFlag } from "@evercam/shared/types"
import CameraUtils from "@/mixins/cameraUtils"
import { AiApi } from "@evercam/shared/api/aiApi"
import { mapStores } from "pinia"
import { useCameraDialogStore } from "@/stores/cameraDialog"
import { useProjectStore } from "@/stores/projects"
import { useReportStore } from "@/stores/report"
import projectUtils from "@/mixins/projectUtils"
import AnprGateReportCreateUpdateAppDialog from "@/components/gateReport/anpr/AnprGateReportCreateUpdateAppDialog.vue"

export default {
  name: "GateReportAnprProjectsConfig",
  components: {
    ReportTable,
    CamerasColumn,
    NdaIcon,
    AnprGateReportCamerasTable,
    AnprGateReportCreateUpdateAppDialog,
  },
  mixins: [CameraUtils, projectUtils],
  data() {
    return {
      projectHeaders,
      selectedCamera: null,
      showDialog: false,
      isEditMode: false,
      appData: null,
    }
  },
  computed: {
    ...mapStores(useCameraDialogStore, useProjectStore, useReportStore),
  },
  methods: {
    async getGateReportProjectsInfos(params) {
      try {
        const items = await AiApi.gateReport.getGateReportProjectsInfos(params)

        return { items }
      } catch (error) {
        console.error("Error fetching gate report projects:", error)
      }
    },
    isNDAProject(item) {
      return item?.featureFlags?.includes(ProjectFeatureFlag.NdaMc)
    },
    getFeatureFlags(item) {
      return (
        item?.featureFlags?.map((feature) => this.toTitleCase(feature)) || []
      )
    },
    onCameraDialogOpen(camera) {
      this.cameraDialogStore.openDialog({ camera: camera.exid })
    },
    appsStatusColorText(status: string) {
      return status.includes("running:")
        ? "green--text text--darken-2"
        : "red--text text--darken-2"
    },
    onEditApp(item, camera) {
      this.appData = {
        project: item.project,
        ...camera,
      }
      this.isEditMode = true
      this.showDialog = true
    },
    onCloseDialog(val) {
      this.showDialog = val
      if (!val) {
        this.isEditMode = false
        this.appData = null
      }
    },
    getProjectLink(item) {
      return `/gate-report/${this.$route.params.report}/${item.project.exid}`
    },
  },
}
</script>

<style scoped>
.icon {
  font-size: 20px !important;
}

.project-item {
  display: grid;
  grid-template-columns: 6fr 1fr;
  min-width: 0;
}
</style>
